import { useEffect, useMemo, useState } from 'react'
import { uniqBy } from 'lodash'
import { AscendFormModal } from 'app/components/integrations/configuration-forms/AscendFormModal'
import { CallRailFormModal } from 'app/components/integrations/configuration-forms/CallRailFormModal'
import { CarestackFormModal } from 'app/components/integrations/configuration-forms/CarestackFormModal'
import { ClickFunnelsFormModal } from 'app/components/integrations/configuration-forms/ClickFunnelsFormModal'
import { DniFormModal } from 'app/components/integrations/configuration-forms/DniFormModal'
import { ElevenLabsFormModal } from 'app/components/integrations/configuration-forms/ElevenLabsFormModal'
import { FacebookAdsFormModal } from 'app/components/integrations/configuration-forms/FacebookAdsFormModal'
import { FacebookLeadsFormModal } from 'app/components/integrations/configuration-forms/FacebookLeadsFormModal'
import { GoogleAdsFormModal } from 'app/components/integrations/configuration-forms/GoogleAdsFormModal'
import { GravityFormModal } from 'app/components/integrations/configuration-forms/GravityFormModal'
import { JotformFormModal } from 'app/components/integrations/configuration-forms/JotformFormModal'
import { KollaFormModal } from 'app/components/integrations/configuration-forms/KollaFormModal'
import { RingCentralFormModal } from 'app/components/integrations/configuration-forms/RingCentralFormModal'
import { SikkaFormModal } from 'app/components/integrations/configuration-forms/SikkaFormModal'
import { TwilioNumbersModal } from 'app/components/integrations/configuration-forms/TwilioNumbersModal'
import { WebflowFormModal } from 'app/components/integrations/configuration-forms/WebflowFormModal'
import useClientSideTableFilters from 'app/hooks/useClientSideTableFilters'
import { useSyncProgress } from 'app/hooks/useSyncProgress'
import { invalidateIntegrationStatuses } from 'app/components/integrations-locations/integrationStatus'
import { showModal } from 'app/lib/modal'
import type { IntegrationsRow } from 'app/routes/__app/settings/integrations/locations'
import type { SyncProgressByLocation } from 'app/types/integrations'
import { filterableColumns } from 'app/table-columns/integrations'
import type { FilterableColumn } from 'app/filter.utils'
import { useUser } from 'app/utils'

export type UseIntegrationsLocationsReturn = {
  rows: IntegrationsRow[]
  filters: FilterableColumn[]
  searchValue: string
  setSearchValue: (val: string) => void
  onIntegrationClick: (row: IntegrationsRow) => void
  isLoading: boolean
  statusEpoch: number
  syncProgress: SyncProgressByLocation
}

export function useIntegrationsLocations(): UseIntegrationsLocationsReturn {
  const user = useUser()
  const [results, setResults] = useState<IntegrationsRow[]>([])
  const [searchValue, setSearchValue] = useState(``)
  const [isLoading, setIsLoading] = useState(true)
  const [statusEpoch, setStatusEpoch] = useState(0)

  const fetchData = () => {
    setIsLoading(true)
    fetch(`/api/integrations/locations-list`, { credentials: `include` })
      .then((res) => res.json())
      .then((data) => setResults(data.results ?? []))
      .catch(() => {})
      .finally(() => setIsLoading(false))
  }

  // Invalidate every cached per-integration status, bump the epoch so the
  // grouped view's resolution effect re-runs even when the row set itself
  // hasn't changed, and re-fetch the row list itself.
  const refreshAll = () => {
    invalidateIntegrationStatuses()
    setStatusEpoch((epoch) => epoch + 1)
    fetchData()
  }

  const syncProgress = useSyncProgress(refreshAll)

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    const handler = () => refreshAll()
    window.addEventListener(`integrations:refetch`, handler)
    return () => window.removeEventListener(`integrations:refetch`, handler)
  }, [])

  function onIntegrationAdded() {
    refreshAll()
  }

  const onIntegrationClick = (integrationsRow: IntegrationsRow) => {
    const modalMap: Record<string, () => void> = {
      Google: () =>
        showModal(
          <GoogleAdsFormModal
            onAdd={onIntegrationAdded}
            currentValues={{
              locationId: integrationsRow.locationId,
              locationName: integrationsRow.locationName,
              googleAdsV3: integrationsRow.googleAdsV3,
            }}
          />,
          { title: `Google Ads`, isFullScreen: false, overflowHidden: false, removePadding: true, useRadix: true },
        ),
      'Facebook Ads': () =>
        showModal(
          <FacebookAdsFormModal
            onAdd={onIntegrationAdded}
            currentValues={{
              locationId: integrationsRow.locationId,
              locationName: integrationsRow.locationName,
              facebookAds: integrationsRow.facebookAds,
            }}
          />,
          { title: `Facebook Ads`, isFullScreen: false, overflowHidden: false, removePadding: true, useRadix: true },
        ),
      'Facebook Leads': () =>
        showModal(
          <FacebookLeadsFormModal
            onAdd={onIntegrationAdded}
            currentValues={{
              locationId: integrationsRow.locationId,
              locationName: integrationsRow.locationName,
              facebookLeadsPages: integrationsRow.facebookLeadsPages,
            }}
          />,
          { title: `Facebook Leads`, isFullScreen: false, overflowHidden: false, removePadding: true, useRadix: true },
        ),
      Jotform: () =>
        showModal(
          <JotformFormModal
            onAdd={onIntegrationAdded}
            currentValues={{
              locationId: integrationsRow.locationId,
              locationName: integrationsRow.locationName,
              jotform: integrationsRow.jotformFormIds || integrationsRow.jotformV2,
            }}
          />,
          { title: `Jotform`, isFullScreen: false, overflowHidden: false, removePadding: true, useRadix: true },
        ),
      CallRail: () =>
        showModal(
          <CallRailFormModal
            onAdd={onIntegrationAdded}
            currentValues={{
              locationId: integrationsRow.locationId,
              locationName: integrationsRow.locationName,
              callRailCompanyId: integrationsRow.callRailCompanyId,
            }}
          />,
          { title: `Callrail`, isFullScreen: false, overflowHidden: false, removePadding: true, useRadix: true },
        ),
      Sikka: () =>
        showModal(
          <SikkaFormModal
            onAdd={onIntegrationAdded}
            currentValues={{
              locationId: integrationsRow.locationId,
              locationName: integrationsRow.locationName,
              sikkaOfficeId: integrationsRow.sikkaOfficeId,
              sikkaPracticeId: integrationsRow.sikkaPracticeId,
            }}
          />,
          { title: `Sikka`, isFullScreen: false, overflowHidden: false, removePadding: true, useRadix: true },
        ),
      Carestack: () =>
        showModal(
          <CarestackFormModal
            onAdd={onIntegrationAdded}
            currentValues={{
              locationId: integrationsRow.locationId,
              locationName: integrationsRow.locationName,
              carestackLocationId: integrationsRow.carestackLocationId,
            }}
          />,
          { title: `Carestack`, isFullScreen: false, overflowHidden: false, removePadding: true, useRadix: true },
        ),
      RingCentral: () =>
        showModal(
          <RingCentralFormModal
            onAdd={onIntegrationAdded}
            currentValues={{
              locationId: integrationsRow.locationId,
              locationName: integrationsRow.locationName,
              ringCentralPhoneNumbers: integrationsRow.ids,
            }}
          />,
          { title: `RingCentral`, isFullScreen: false, overflowHidden: false, removePadding: true, useRadix: true },
        ),
      Kolla: () =>
        showModal(
          <KollaFormModal
            onAdd={onIntegrationAdded}
            currentValues={{
              locationId: integrationsRow.locationId,
              locationName: integrationsRow.locationName,
              kollaConnectorId: integrationsRow.ids[0],
              kollaConsumerId: integrationsRow.ids[1],
              kollaPracticeId: integrationsRow.kollaPracticeId,
            }}
          />,
          { title: `Kolla`, isFullScreen: false, overflowHidden: false, removePadding: true, useRadix: true },
        ),
      'Gravity Forms': () =>
        showModal(
          <GravityFormModal
            onAdd={onIntegrationAdded}
            currentValues={{
              locationId: integrationsRow.locationId,
              locationName: integrationsRow.locationName,
              gravityFormsKey: integrationsRow.ids[0],
            }}
          />,
          { title: `Gravity Forms`, isFullScreen: false, overflowHidden: false, removePadding: true, useRadix: true },
        ),
      ClickFunnels: () =>
        showModal(
          <ClickFunnelsFormModal
            onAdd={onIntegrationAdded}
            currentValues={{
              locationId: integrationsRow.locationId,
              locationName: integrationsRow.locationName,
              clickFunnelsConfig: integrationsRow.clickFunnelsConfig,
              webhookUrl: integrationsRow.webhookUrl,
            }}
          />,
          { title: `ClickFunnels`, isFullScreen: false, overflowHidden: false, removePadding: true, useRadix: true },
        ),
      'Webflow Forms': () =>
        showModal(
          <WebflowFormModal
            onAdd={onIntegrationAdded}
            currentValues={{
              locationId: integrationsRow.locationId,
              locationName: integrationsRow.locationName,
              webflowFormsKey: integrationsRow.ids[0],
            }}
          />,
          { title: `Webflow Forms`, isFullScreen: false, overflowHidden: false, removePadding: true, useRadix: true },
        ),
      Twilio: () =>
        showModal(
          <TwilioNumbersModal
            onAdd={onIntegrationAdded}
            currentValues={{
              locationId: integrationsRow.locationId,
              locationName: integrationsRow.locationName,
              twilioNumbers: integrationsRow.twilioNumbers,
            }}
          />,
          { title: `Twilio`, isFullScreen: false, overflowHidden: false, removePadding: true, useRadix: true },
        ),
      Ascend: () =>
        showModal(
          <AscendFormModal
            onAdd={onIntegrationAdded}
            currentValues={{
              locationId: integrationsRow.locationId,
              locationName: integrationsRow.locationName,
              ascendLocationId: integrationsRow.ascendLocationId,
            }}
          />,
          { title: `Ascend`, isFullScreen: false, overflowHidden: false, removePadding: true, useRadix: true },
        ),
      'Dynamic Number Insertion': () =>
        showModal(
          <DniFormModal
            onAdd={onIntegrationAdded}
            dniId={integrationsRow.dniId!}
            locationId={integrationsRow.locationId}
            locationName={integrationsRow.locationName}
          />,
          {
            title: `Dynamic Number Insertion - DNI`,
            isFullScreen: false,
            overflowHidden: false,
            removePadding: true,
            useRadix: true,
          },
        ),
      ...(user?.type === `super` && {
        ElevenLabs: () =>
          showModal(
            <ElevenLabsFormModal
              onAdd={onIntegrationAdded}
              locationId={integrationsRow.locationId}
              locationName={integrationsRow.locationName}
              elevenLabsAgentId={integrationsRow.elevenLabsAgentId}
            />,
            { title: `ElevenLabs`, isFullScreen: false, overflowHidden: false, removePadding: true, useRadix: true },
          ),
      }),
    }

    modalMap[integrationsRow.integration]?.()
  }

  const filters = useMemo(
    () =>
      filterableColumns(
        uniqBy(
          results.map((row) => ({ id: row.locationId, value: row.locationId, name: row.locationName })),
          `id`,
        ),
      ),
    [results],
  )

  const rows = useClientSideTableFilters(
    results,
    filters,
    `key`,
    [`locationName`, `status`, `integration`],
    searchValue,
  )

  return {
    rows: rows as IntegrationsRow[],
    filters,
    searchValue,
    setSearchValue,
    onIntegrationClick,
    isLoading,
    statusEpoch,
    syncProgress,
  }
}
